/**
 * RolePreview — live "what does this role grant" panel.
 *
 * Usage:
 *   <RolePreview role="sales_agent" />                           // invite modal
 *   <RolePreview role={newRole} priorRole={currentRole} />       // edit-role with diff
 *
 * Accepts an optional `matrix` prop (from GET /admin/rbac/matrix) so parents
 * can load once and pass it down. Falls back to its own fetch on mount.
 *
 * Also surfaces delegation warnings:
 *   - whether the role can invite/admin others
 *   - whether the current caller can assign this role (from assignable-roles)
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Check, X, AlertTriangle, Shield, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RolePreview({ role, priorRole, matrix, assignableRoles }) {
  const [data, setData] = useState(matrix || null);
  const [assignable, setAssignable] = useState(assignableRoles || null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (matrix) { setData(matrix); return; }
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/admin/rbac/matrix`, { headers: { Authorization: `Bearer ${token}` } });
        if (!alive) return;
        setData(res.data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Matrix unavailable");
      }
    })();
    return () => { alive = false; };
  }, [matrix]);

  useEffect(() => {
    if (assignableRoles) { setAssignable(assignableRoles); return; }
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/admin/delegation/assignable-roles`, { headers: { Authorization: `Bearer ${token}` } });
        if (alive) setAssignable(res.data?.assignable || []);
      } catch (_) {
        if (alive) setAssignable([]);
      }
    })();
    return () => { alive = false; };
  }, [assignableRoles]);

  const diff = useMemo(() => {
    if (!data || !role) return null;
    const curCaps = data.matrix[role] || {};
    const prevCaps = priorRole ? (data.matrix[priorRole] || {}) : null;
    const groups = {};
    for (const cap of data.capabilities) {
      const cur = !!curCaps[cap.key];
      const prev = prevCaps ? !!prevCaps[cap.key] : cur;
      let change = "same";
      if (prevCaps) {
        if (cur && !prev) change = "gained";
        else if (!cur && prev) change = "lost";
      }
      const row = { ...cap, granted: cur, prior: prev, change };
      const g = cap.group || "Other";
      (groups[g] = groups[g] || []).push(row);
    }
    const gainedCount = Object.values(groups).flat().filter(r => r.change === "gained").length;
    const lostCount = Object.values(groups).flat().filter(r => r.change === "lost").length;
    const grantedCount = Object.values(groups).flat().filter(r => r.granted).length;
    return { groups, gainedCount, lostCount, grantedCount };
  }, [data, role, priorRole]);

  if (err) return <div data-testid="role-preview-error" style={{ padding: 10, background: "#FFEBEB", color: "#A32D2D", borderRadius: 6, fontSize: 11 }}>{err}</div>;
  if (!data || !role) return <div data-testid="role-preview-loading" style={{ padding: 10, color: "#888", fontSize: 11 }}>Loading role preview…</div>;

  const delegationPower = (data.delegation?.powers || {})[role];
  const isAdmin = role === "super_admin" || role === "crm_admin";
  const canBeAssigned = !assignable || assignable.length === 0 ? null : assignable.includes(role);

  return (
    <div data-testid="role-preview" data-role={role} style={{ border: "0.5px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ background: "var(--page-bg)", padding: "8px 12px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 650 }}>
            Role preview: <span data-testid="role-preview-label">{role.replace(/_/g, " ")}</span>
          </div>
          <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 2 }}>
            <span data-testid="role-preview-grant-count">{diff?.grantedCount || 0}</span> capabilities granted
            {priorRole && priorRole !== role && (
              <>
                {" "}· <span data-testid="role-preview-gained" style={{ color: "#0F6E56", fontWeight: 600 }}>+{diff?.gainedCount || 0} gained</span>
                {" "}· <span data-testid="role-preview-lost" style={{ color: "#A32D2D", fontWeight: 600 }}>−{diff?.lostCount || 0} lost</span>
              </>
            )}
          </div>
        </div>
        {canBeAssigned === false && (
          <div data-testid="role-preview-not-assignable" style={{ background: "#FFEBEB", color: "#A32D2D", fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 8 }}>
            YOU CANNOT ASSIGN THIS ROLE
          </div>
        )}
      </div>

      {(isAdmin || delegationPower) && (
        <div data-testid="role-preview-delegation" style={{ background: "#FFF8F0", borderBottom: "0.5px solid #FAC775", padding: "8px 12px", fontSize: 11, color: "#854F0B", display: "flex", alignItems: "flex-start", gap: 6 }}>
          <AlertTriangle style={{ width: 12, height: 12, marginTop: 2 }} />
          <div>
            <strong>Administrative role</strong> — grants delegation powers:
            <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
              {delegationPower?.invite_any_role && <li>Invite <strong>any role</strong>, including other super_admins</li>}
              {delegationPower?.invite_non_admin_roles && <li>Invite any non-admin role</li>}
              {delegationPower?.reset_mfa && <li>Reset other users' MFA</li>}
              {delegationPower?.manage_profiles && <li>Create + manage CSV mapping profiles</li>}
            </ul>
          </div>
        </div>
      )}

      <div style={{ maxHeight: 320, overflow: "auto" }}>
        {Object.entries(diff?.groups || {}).map(([g, rows]) => (
          <div key={g}>
            <div style={{ padding: "6px 12px", fontSize: 10, fontWeight: 700, color: "#2563eb", background: "#f9faff", textTransform: "uppercase", letterSpacing: 0.4 }}>{g}</div>
            {rows.map(r => (
              <div
                key={r.key}
                data-testid={`role-preview-cap-${r.key}`}
                data-change={r.change}
                data-granted={r.granted}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 12px", borderBottom: "0.5px solid var(--border)", fontSize: 11, background: r.change === "gained" ? "#DAFBE820" : r.change === "lost" ? "#FFEBEB20" : "transparent" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span title={r.description} style={{ color: "var(--text-secondary)" }}>{r.label || r.key}</span>
                  {(r.flags || []).includes("admin") && <span style={{ background: "#FFF8F0", color: "#854F0B", padding: "0 5px", borderRadius: 6, fontSize: 9, fontWeight: 700 }}>ADMIN</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {priorRole && priorRole !== role ? (
                    <>
                      <StatusIcon granted={r.prior} />
                      <ArrowRight style={{ width: 10, height: 10, color: "#888" }} />
                      <StatusIcon granted={r.granted} />
                      {r.change !== "same" && <span style={{ fontSize: 9, fontWeight: 700, padding: "0 5px", borderRadius: 6, background: r.change === "gained" ? "#DAFBE8" : "#FFEBEB", color: r.change === "gained" ? "#0F6E56" : "#A32D2D" }}>{r.change.toUpperCase()}</span>}
                    </>
                  ) : (
                    <StatusIcon granted={r.granted} />
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusIcon({ granted }) {
  return granted
    ? <Check style={{ width: 12, height: 12, color: "#0F6E56" }} />
    : <X style={{ width: 12, height: 12, color: "#bbb" }} />;
}
